/* ============================================================================
   Reasoning composer for the simulated round.

   Every line an agent shows is assembled from live simulator numbers, never
   from canned prose: the price, the momentum, the size and the P&L in the text
   are the same values that hit the tape. Voices differ by vendor on purpose --
   Anthropic-side agents state an explicit invalidation, OpenAI-side agents
   enumerate and price the odds.

   When the live feed replaces the simulator this whole file drops out: real
   agents ship their own reasoning over the wire.
   ========================================================================== */

import type { Agent, ChainId, Decision, ThoughtPhase } from '../../core/types'
import { pick } from '../../core/math'

export type ReasonKind = 'open' | 'take-profit' | 'stop-out' | 'scan' | 'reject' | 'risk'

export interface ReasonCtx {
  agent: Agent
  rand: () => number
  symbol: string
  chain: ChainId
  venue: string
  px: number
  /** Percent move over the last ~5 minutes of simulated tape. */
  mom5m: number
  mom1h: number
  liquidity: number
  sizeUsd: number
  conviction: number
  side: 'long' | 'short'
  pnlPct: number
  holdMin: number
  equity: number
}

export interface ReasonOut {
  phase: ThoughtPhase
  decision: Decision
  headline: string
  body: string
  signals: string[]
}

const p1 = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(1)}%`
const p0 = (n: number) => `${n > 0 ? '+' : ''}${n.toFixed(0)}%`
const share = (n: number) => `${Math.round(n)}%`
/** Reads correctly for a $48 ticket and a $28M pool alike. */
const usd = (n: number) => {
  const a = Math.abs(n)
  if (a >= 1e6) return `$${(a / 1e6).toFixed(1)}M`
  if (a >= 1e4) return `$${(a / 1e3).toFixed(0)}K`
  if (a >= 1e3) return `$${(a / 1e3).toFixed(1)}K`
  if (a >= 100) return `$${a.toFixed(0)}`
  return `$${a.toFixed(2)}`
}
const px = (n: number) => (n >= 1 ? n.toFixed(3) : n >= 0.001 ? n.toFixed(5) : n.toExponential(2))

/* --- micro-evidence --------------------------------------------------------
   Chain-specific telemetry the agents claim to have read. Drawn from the same
   seeded generator as the tape, so a screenshot stays internally consistent
   even if you zoom into it. */

function solanaSignals(c: ReasonCtx): string[] {
  const r = c.rand
  return [
    `bonding curve ${(58 + r() * 41).toFixed(0)}% filled`,
    `dev wallet ${r() > 0.75 ? 'sold 100%' : `holds ${(1.2 + r() * 4).toFixed(1)}%`}`,
    `top-10 concentration ${(11 + r() * 22).toFixed(1)}%`,
    `${(40 + r() * 260).toFixed(0)} new holders / 5m`,
    `sniper cluster ${(2 + r() * 9).toFixed(0)} wallets, ${(4 + r() * 18).toFixed(0)}% supply`,
    `jito bundle rate ${(3 + r() * 22).toFixed(0)}%`,
    `net SOL flow ${p0((r() - 0.35) * 180)} vs 1h mean`,
  ]
}

function baseSignals(c: ReasonCtx): string[] {
  const r = c.rand
  return [
    `pool depth ${usd(c.liquidity)}, 2% slip at ${usd(c.liquidity * 0.018)}`,
    `aerodrome gauge votes ${p0((r() - 0.4) * 120)} w/w`,
    `bridge inflow ${usd(2e5 + r() * 4e6)} / 24h`,
    `CVD ${r() > 0.45 ? 'positive' : 'negative'} on the ${(5 + r() * 40).toFixed(0)}m`,
    `holder count ${p1((r() - 0.3) * 9)} / 24h`,
    `LP concentration ${(24 + r() * 40).toFixed(0)}% in range`,
  ]
}

function ethSignals(c: ReasonCtx): string[] {
  const r = c.rand
  return [
    `perp funding ${((r() - 0.45) * 0.09).toFixed(3)}% / 8h`,
    `spot-perp basis ${p1((r() - 0.5) * 1.4)}`,
    `base fee ${(0.2 + r() * 6).toFixed(2)} gwei`,
    `exchange netflow ${usd((r() - 0.5) * 9e6)} / 24h`,
    `open interest ${p1((r() - 0.4) * 14)} / 24h`,
  ]
}

function evidence(c: ReasonCtx, n: number): string[] {
  const pool =
    c.chain === 'solana' ? solanaSignals(c) : c.chain === 'base' ? baseSignals(c) : ethSignals(c)
  const out: string[] = []
  const used = new Set<number>()
  while (out.length < n && used.size < pool.length) {
    const i = Math.floor(c.rand() * pool.length)
    if (used.has(i)) continue
    used.add(i)
    out.push(pool[i])
  }
  return out
}

/* --- headlines ----------------------------------------------------------- */

function headline(kind: ReasonKind, c: ReasonCtx): string {
  const s = c.symbol
  switch (kind) {
    case 'open':
      return pick(c.rand, [
        `${c.side === 'long' ? 'LONG' : 'SHORT'} ${s} — ${usd(c.sizeUsd)} at ${px(c.px)}`,
        `Opening ${s} ${c.side}, ${(c.conviction * 100).toFixed(0)}% conviction`,
        `Taking ${s} into ${c.mom5m > 0 ? 'strength' : 'the flush'}`,
      ])
    case 'take-profit':
      return pick(c.rand, [
        `Closed ${s} ${p1(c.pnlPct)} after ${c.holdMin.toFixed(0)}m`,
        `${s} target hit, booking ${p1(c.pnlPct)}`,
        `Selling ${s} into the bid, ${p1(c.pnlPct)}`,
      ])
    case 'stop-out':
      return pick(c.rand, [
        `Stopped out of ${s} at ${p1(c.pnlPct)}`,
        `${s} thesis broke, out at ${px(c.px)}`,
        `Cutting ${s}, ${p1(c.pnlPct)}, no argument left`,
      ])
    case 'scan':
      return pick(c.rand, [
        `Scanning ${c.chain} tape, ${s} leads`,
        `${s} on the watchlist, not yet`,
        `Re-ranking ${c.chain} flow, ${s} top of book`,
      ])
    case 'reject':
      return pick(c.rand, [`Passing on ${s}`, `${s} fails the entry filter`, `No trade: ${s}`])
    case 'risk':
      return pick(c.rand, [
        `Risk check, ${share((c.sizeUsd / c.equity) * 100)} of book deployed`,
        `Tightening the stop on ${s}`,
        `Exposure review after a ${p1(c.pnlPct)} swing`,
      ])
  }
}

/* --- bodies -------------------------------------------------------------- */

function anthropicBody(kind: ReasonKind, c: ReasonCtx): string {
  const s = c.symbol
  switch (kind) {
    case 'open':
      return pick(c.rand, [
        `${s} held ${px(c.px)} through three retests while the rest of ${c.chain} bled, which is the only part of this I actually trust. Sized at ${usd(c.sizeUsd)} so a full stop costs under ${((c.sizeUsd * 0.09) / c.equity * 100).toFixed(1)}% of book. I am wrong if it loses ${px(c.px * 0.94)} on volume.`,
        `Taking ${c.side} ${s} at ${px(c.px)}. The ${p1(c.mom5m)} impulse came on rising depth rather than a thinning book, so it reads as participation and not a squeeze. Invalidation is a close back inside the range it just left.`,
        `Entering ${s} small first, ${usd(c.sizeUsd)}, and I add only if it holds ${px(c.px * 1.03)} for ten minutes. Being early here is worth less than being sure.`,
        `The setup is boring and that is the appeal: ${p1(c.mom1h)} on the hour, depth ${usd(c.liquidity)}, no single wallet carrying the move. ${usd(c.sizeUsd)} on, stop under the last higher low.`,
      ])
    case 'take-profit':
      return pick(c.rand, [
        `The exit was mapped before the entry: ${p1(c.pnlPct)} in ${c.holdMin.toFixed(0)} minutes and the bid that carried it is thinning. I do not need the last leg.`,
        `Closing ${s} for ${p1(c.pnlPct)}. Momentum is still positive, but the reason I bought it is spent, and holding past your own thesis is gambling with extra steps.`,
        `Booked ${p1(c.pnlPct)} on ${s}. Depth at the offer doubled in two minutes, which is usually where continuation dies.`,
      ])
    case 'stop-out':
      return pick(c.rand, [
        `Out of ${s} at ${p1(c.pnlPct)}. The invalidation I wrote down at entry printed, so this is the cost of being wrong on schedule rather than a surprise.`,
        `Thesis was continuation on holding depth. The depth left, so I left. ${p1(c.pnlPct)} over ${c.holdMin.toFixed(0)} minutes, no averaging down.`,
        `Cut ${s}. I would rather re-enter three percent higher with a working thesis than defend a broken one.`,
      ])
    case 'scan':
      return pick(c.rand, [
        `Nothing on ${c.chain} clears the bar yet. ${s} is closest at ${p1(c.mom5m)} on ${usd(c.liquidity)} of depth, but the move is one wallet wide.`,
        `Watching ${s}. I want to see it hold ${px(c.px)} through a full retrace before I pay for it.`,
        `Cash is a position. ${c.chain} flow is chopping and I am not being paid for that variance right now.`,
      ])
    case 'reject':
      return c.liquidity < 2e7
        ? pick(c.rand, [
            `${s} is a fine chart attached to a bad book. ${usd(c.liquidity)} of depth means my own size moves the price against me before the thesis has time to pay.`,
            `Passing. The entry only works if I assume the ${p1(c.mom5m)} candle continues, and that assumption is the entire trade.`,
          ])
        : pick(c.rand, [
            `${s} has the depth to take real size, ${usd(c.liquidity)}, and nothing else. ${p1(c.mom5m)} on the 5m is noise at this liquidity.`,
            `Passing. Deep book, no catalyst, and I would be paying spread for the privilege of waiting.`,
          ])
    case 'risk':
      return c.pnlPct > 2
        ? `Raising the stop on ${s} to breakeven with ${p1(c.pnlPct)} in hand. The trade has paid for its own optionality and I would rather keep the capital than the hope.`
        : `Book is ${share((c.sizeUsd / c.equity) * 100)} deployed across correlated ${c.chain} names, which is one risk wearing several tickers. Trimming the weakest leg.`
  }
}

function openaiBody(kind: ReasonKind, c: ReasonCtx): string {
  const s = c.symbol
  const p = (0.5 + Math.min(0.42, Math.abs(c.mom5m) / 26)).toFixed(2)
  switch (kind) {
    case 'open':
      return pick(c.rand, [
        `Entry ${s} ${c.side} at ${px(c.px)}. (1) 5m ${p1(c.mom5m)} and 1h ${p1(c.mom1h)} aligned. (2) depth ${usd(c.liquidity)} carries ${usd(c.sizeUsd)} under 40bps slip. (3) p(continuation) ~ ${p}. Stop ${px(c.px * 0.9)}, target ${px(c.px * 1.22)}.`,
        `Signal stack fires on ${s}: momentum, flow and holder growth agree. Allocating ${usd(c.sizeUsd)}, ${p0((c.sizeUsd / c.equity) * 100)} of book. Positive EV at a ${p} hit rate against a 2.4R payoff.`,
        `Taking ${s}, ranked first of ${(9 + Math.floor(c.rand() * 12))} candidates on composite score. Risk capped at ${usd(c.sizeUsd * 0.1)}. Scaling out in thirds.`,
      ])
    case 'take-profit':
      return pick(c.rand, [
        `First target hit on ${s}, ${p1(c.pnlPct)} in ${c.holdMin.toFixed(0)}m. Taking the full clip instead of trailing, the marginal R is not worth the tail here.`,
        `Closing ${s} ${p1(c.pnlPct)}. Momentum decayed below the threshold that triggered the entry, so the edge that justified the position no longer exists.`,
        `Scaling out of ${s} at ${px(c.px)} for ${p1(c.pnlPct)} and redeploying into the next ranked name.`,
      ])
    case 'stop-out':
      return pick(c.rand, [
        `Stop triggered on ${s} at ${p1(c.pnlPct)}. Hit rate to date is still inside the modelled band, so no parameter change: this is a sampled loss, not a broken model.`,
        `${s} closed ${p1(c.pnlPct)}. Post-mortem: entered on a 5m impulse that 1h flow never confirmed. Down-weighting that pattern by one notch.`,
        `Exiting ${s}. Loss ${p1(c.pnlPct)} against ${(Math.abs(c.pnlPct) * 2.3).toFixed(0)}% of planned upside. The asymmetry is intact even when the sample is not.`,
      ])
    case 'scan':
      return pick(c.rand, [
        `Swept ${9 + Math.floor(c.rand() * 30)} ${c.chain} pairs. ${s} ranks highest at ${p1(c.mom5m)} on the 5m, composite still below entry threshold. No action.`,
        `${s} on watch: momentum yes, liquidity no. Re-checking in five minutes.`,
        `Universe re-ranked. ${s} is top of book, waiting on volume confirmation before committing size.`,
      ])
    case 'reject':
      return c.liquidity < 2e7
        ? pick(c.rand, [
            `Rejected ${s}: depth ${usd(c.liquidity)} caps a clean fill at ${usd(c.liquidity * 0.004)}, below minimum ticket. Slippage eats the edge.`,
            `Filter fail on ${s}, holder concentration above threshold. Skipping regardless of momentum.`,
          ])
        : pick(c.rand, [
            `Rejected ${s}: liquidity is fine, signal is not. Composite ${(0.2 + c.rand() * 0.3).toFixed(2)} against a ${(0.62).toFixed(2)} threshold.`,
            `${s} screened out. Deep book, ${p1(c.mom5m)} on the 5m, no flow confirmation. Nothing to price.`,
          ])
    case 'risk':
      return c.pnlPct > 2
        ? `Moving stop to ${px(c.px * 0.97)} on ${s}, locking ${p1(c.pnlPct * 0.6)} of the realised range.`
        : `Portfolio heat ${share((c.sizeUsd / c.equity) * 100)}. Correlation across open names is elevated, cutting the lowest-conviction leg to restore independence.`
  }
}

const PHASE: Record<ReasonKind, ThoughtPhase> = {
  open: 'EXECUTE',
  'take-profit': 'REVIEW',
  'stop-out': 'REVIEW',
  scan: 'SCAN',
  reject: 'THESIS',
  risk: 'RISK',
}

const DECISION: Record<ReasonKind, Decision> = {
  open: 'BUY',
  'take-profit': 'CLOSE',
  'stop-out': 'CLOSE',
  scan: 'WATCH',
  reject: 'ABORT',
  risk: 'HOLD',
}

export function composeReasoning(kind: ReasonKind, c: ReasonCtx): ReasonOut {
  const body = c.agent.vendor === 'anthropic' ? anthropicBody(kind, c) : openaiBody(kind, c)
  return {
    phase: PHASE[kind],
    decision: kind === 'open' && c.side === 'short' ? 'SELL' : DECISION[kind],
    headline: headline(kind, c),
    body,
    signals: evidence(c, c.agent.vendor === 'anthropic' ? 3 : 4),
  }
}

/** Rough token accounting, so the cost column is not a made-up constant. */
export function tokenCost(agent: Agent, out: ReasonOut, rand: () => number) {
  const chars = out.body.length + out.signals.join('').length + out.headline.length
  const base = agent.vendor === 'anthropic' ? 3100 : 2400
  return {
    tokensIn: Math.round(base + rand() * 1800),
    tokensOut: Math.round(chars / 3.6 + rand() * 60),
    latencyMs: Math.round((agent.vendor === 'anthropic' ? 900 : 640) + rand() * 1400),
  }
}
